import React from 'react';

/**
 * COMPONENTE PANTALLA GIGANTE: PORTADA DE SECUENCIA (SIMON SAYS)
 * Renderiza la carátula cinematográfica de bienvenida para la dinámica Secuencia en el estadio.
 * Diseñado bajo arquitectura Zero-Canvas con aceleración por hardware GPU (CSS3 transforms,
 * radial-gradient oscuro, texto brillante con animación glow y dots de color sincronizados).
 * 
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string} [props.title='Memoriza la secuencia'] - Título principal de la dinámica.
 * @param {string} [props.sponsorLogo='/img/page/xolos-five.png'] - Ruta del logo del patrocinador.
 * @returns {JSX.Element} Vista de portada de Secuencia.
 */
const FrontPageSequence = ({ 
    title = 'Memoriza la secuencia', 
    sponsorLogo = '/img/page/xolos-five.png' 
}) => {
    // Estilos visuales con aceleración GPU (Zero-Canvas)
    const customStyles = `
        .sequence-intro {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            width: 100vw;
            background: radial-gradient(circle, #0e0e1a 0%, #05050a 100%);
            color: #fff;
            font-family: 'Outfit', 'Inter', sans-serif;
            text-align: center;
            overflow: hidden;
            position: relative;
            user-select: none;
        }
        .sequence-intro__logo {
            max-width: 400px;
            margin-bottom: 2rem;
            animation: pulse-logo 3s infinite alternate;
            will-change: transform, filter;
        }
        .sequence-intro__title {
            font-size: 5.5rem;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 4px;
            background: linear-gradient(90deg, #00ff41, #00e1ff, #b300ff);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            text-shadow: 0 0 30px rgba(0, 225, 255, 0.3);
            margin: 0;
            animation: glow-title 1.5s ease-in-out infinite alternate;
            will-change: text-shadow;
        }
        .sequence-intro__subtitle {
            font-size: 1.8rem;
            color: #888899;
            text-transform: uppercase;
            letter-spacing: 6px;
            margin-top: 1rem;
            font-weight: 600;
        }
        .sequence-intro__decor {
            display: flex;
            gap: 20px;
            margin-top: 3rem;
        }
        .sequence-intro__dot {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 3px solid #fff;
            animation: flash-decor 1.2s infinite;
            will-change: transform, opacity;
        }
        .sequence-intro__dot:nth-child(1) { background-color: #00ff41; animation-delay: 0.0s; box-shadow: 0 0 20px #00ff41; }
        .sequence-intro__dot:nth-child(2) { background-color: #ff003c; animation-delay: 0.2s; box-shadow: 0 0 20px #ff003c; }
        .sequence-intro__dot:nth-child(3) { background-color: #fffb00; animation-delay: 0.4s; box-shadow: 0 0 20px #fffb00; }
        .sequence-intro__dot:nth-child(4) { background-color: #00e1ff; animation-delay: 0.6s; box-shadow: 0 0 20px #00e1ff; }
        .sequence-intro__dot:nth-child(5) { background-color: #b300ff; animation-delay: 0.8s; box-shadow: 0 0 20px #b300ff; }
        .sequence-intro__dot:nth-child(6) { background-color: #ff8800; animation-delay: 1.0s; box-shadow: 0 0 20px #ff8800; }

        @keyframes pulse-logo {
            0% { transform: scale(0.98); filter: drop-shadow(0 0 10px rgba(0,225,255,0.2)); }
            100% { transform: scale(1.02); filter: drop-shadow(0 0 25px rgba(0,225,255,0.5)); }
        }
        @keyframes glow-title {
            0% { text-shadow: 0 0 30px rgba(0, 225, 255, 0.2), 0 0 60px rgba(0, 225, 255, 0.1); }
            100% { text-shadow: 0 0 45px rgba(0, 225, 255, 0.4), 0 0 90px rgba(0, 225, 255, 0.2); }
        }
        @keyframes flash-decor {
            0%, 100% { transform: scale(1); opacity: 0.4; }
            50% { transform: scale(1.2); opacity: 1; }
        }
    `;

    return (
        <div className="sequence-intro">
            <style>{customStyles}</style>
            
            {/* Logo oficial con fallback seguro */}
            <img 
                src={sponsorLogo} 
                className="sequence-intro__logo" 
                alt="Sponsor Logo" 
                onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = '/img/page/xolos-five.png'; 
                }} 
            />

            <p className="briefcase-intro__text text-uppercase fw-bold" style={{ color: '#888899', letterSpacing: '3px' }}>
                Presenta
            </p>

            {/* Título de la Dinámica con Gradiente y Glow */}
            <h1 className="sequence-intro__title">{title}</h1>

            {/* Dots decorativos parpadeantes con los 6 colores neón de la dinámica */}
            <div className="sequence-intro__decor">
                <span className="sequence-intro__dot"></span>
                <span className="sequence-intro__dot"></span>
                <span className="sequence-intro__dot"></span>
                <span className="sequence-intro__dot"></span>
                <span className="sequence-intro__dot"></span>
                <span className="sequence-intro__dot"></span>
            </div>

            {/* Créditos de pie de página */}
            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', opacity: 0.5 }}>
                Hecho con ♥ por Interaktia | 2026
            </p>
        </div>
    );
};

export { FrontPageSequence };
export default FrontPageSequence;
